import { useEffect, useMemo, useRef, useState } from "react";
import { Icon, TextField, useDismiss } from "@jkast90/kast-common/ui";
import type { GuideSection } from "../loader";
import "./GuideBrowser.css";

interface GuideBrowserProps {
  sections: GuideSection[];
  currentSectionId: string | null;
  onSelectSection: (sectionId: string) => void;
}

export function GuideBrowser({ sections, currentSectionId, onSelectSection }: GuideBrowserProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const activeItem = useRef<HTMLButtonElement>(null);

  useDismiss(open, root, () => setOpen(false));
  useEffect(() => {
    if (open) activeItem.current?.scrollIntoView({ block: "center" });
  }, [open]);

  const filteredSections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];
    return sections.filter(
      (section) =>
        section.title.toLowerCase().includes(query) ||
        section.location.toLowerCase().includes(query) ||
        section.markdown.toLowerCase().includes(query),
    );
  }, [sections, searchQuery]);

  const currentIndex = sections.findIndex((s) => s.id === currentSectionId);
  const currentSection = sections[currentIndex];
  const searching = searchQuery.trim() !== "";

  const go = (index: number) => {
    const section = sections[index];
    if (section) onSelectSection(section.id);
  };

  return (
    <div className="guide-browser" ref={root}>
      <div className="guide-browser-bar">
        <button
          type="button"
          className="btn small guide-browser-nav-btn"
          disabled={currentIndex <= 0}
          onClick={() => go(currentIndex - 1)}
          aria-label="Previous section"
          title="Previous"
        >
          ←
        </button>
        <button
          type="button"
          className="guide-browser-current"
          aria-expanded={open}
          aria-haspopup="listbox"
          title="Jump to a section"
          onClick={() => setOpen((o) => !o)}
        >
          {currentSection ? (
            <>
              <span className="guide-browser-title kui-truncate">{currentSection.title}</span>
              <span className="guide-browser-nav-count">
                {currentIndex + 1}/{sections.length}
              </span>
            </>
          ) : (
            <span className="guide-browser-title kui-truncate">Pick a section</span>
          )}
          <Icon name={open ? "expand_less" : "expand_more"} size={20} className="icon" />
        </button>
        <button
          type="button"
          className="btn small guide-browser-nav-btn"
          disabled={currentIndex >= sections.length - 1}
          onClick={() => go(currentIndex + 1)}
          aria-label="Next section"
          title="Next"
        >
          →
        </button>
      </div>

      <div className="guide-browser-search">
        <TextField
          variant="bare"
          type="text"
          placeholder="Search guide…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="guide-browser-input"
          aria-label="Search guide sections"
        />
        {searchQuery && (
          <button type="button" className="guide-browser-clear" onClick={() => setSearchQuery("")} title="Clear search">
            ✕
          </button>
        )}
      </div>

      {open && (
        <div className="guide-browser-drawer" role="listbox" aria-label="Guide sections">
          {sections.map((section) => {
            const active = section.id === currentSectionId;
            return (
              <button
                key={section.id}
                ref={active ? activeItem : undefined}
                type="button"
                role="option"
                aria-selected={active}
                className={`guide-browser-item ${active ? "active" : ""}`}
                onClick={() => {
                  onSelectSection(section.id);
                  setOpen(false);
                }}
              >
                <span className="guide-browser-order">{section.order}</span>
                <span className="guide-browser-title kui-truncate">{section.title}</span>
              </button>
            );
          })}
        </div>
      )}

      {searching && (
        <div className="guide-browser-list">
          {filteredSections.length > 0 ? (
            filteredSections.map((section) => (
              <button
                key={section.id}
                type="button"
                className={`guide-browser-item ${section.id === currentSectionId ? "active" : ""}`}
                onClick={() => {
                  onSelectSection(section.id);
                  setSearchQuery("");
                }}
                title={section.location}
              >
                <span className="guide-browser-order">{section.order}</span>
                <span className="guide-browser-title kui-truncate">{section.title}</span>
              </button>
            ))
          ) : (
            <p className="guide-browser-empty">No sections match your search.</p>
          )}
        </div>
      )}
    </div>
  );
}
